import type {
    VirtualStructureEntryItem,
    ConversionDal,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    PluginHeuristicsRegistry,
    StructureIterators,
    StructureStageData
} from '../../types'
import {createVirtualGroupMap, groupingAIResult, reconcileMissingChildren} from './groupingMap'
import type {GroupingApiPlugin, GroupingResult, SectionData, SendRequest} from './types'
import {CTX_AI_GROUPING_RESULT, CTX_SERVER, CTX_SECTION_DATA} from './constants'
import {ReportableError, ReportableWarning} from '@wix/document-manager-utils'
import type {CoreLogger} from '@wix/document-manager-core'
import _ from 'lodash'
import {getStructurePointer, getVirtualStructurePointer} from '../../conversionDal/utils/pointer'
import {VirtualStructureTag} from '../../enums'

export const createPlugin = (pluginInitValues: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const {stages} = pluginInitValues

    const createApi = (context: PluginContext): GroupingApiPlugin => {
        return {
            registerServer: (server: SendRequest) => {
                context.set(CTX_SERVER, server)
            },
            getAiGroupTree: (): GroupingResult => {
                return context.get(CTX_AI_GROUPING_RESULT)
            },
            getSectionDataInput: (): SectionData => {
                return context.get(CTX_SECTION_DATA)
            }
        }
    }

    const validateChildParent = (
        mobileConversionDal: ConversionDal,
        logger: CoreLogger,
        parentId: string,
        realChildrenOrdered: string[]
    ) => {
        const errors: ReportableWarning[] = []
        realChildrenOrdered.forEach(child => {
            const childParent = mobileConversionDal.get(getStructurePointer({id: child})).parent
            if (!_.isEqual(childParent, parentId)) {
                const error = new ReportableWarning({
                    message: `child is assigned to intruder`,
                    errorType: 'groupingIntruder',
                    extras: {
                        componentId: child,
                        intruder: parentId
                    }
                })
                logger.captureError(error)
                errors.push(error)
            }
        })

        return errors
    }

    const validateGroupingResult = (
        mobileConversionDal: ConversionDal,
        structureIterators: StructureIterators,
        componentToChildrenGroupMap: Record<string, Partial<VirtualStructureEntryItem>>
    ): boolean => {
        let errors: ReportableError[] = []

        structureIterators.forEachComponent(pointer => {
            errors = errors.concat(
                validateChildParent(
                    mobileConversionDal,
                    pluginInitValues.logger,
                    pointer.id,
                    componentToChildrenGroupMap[pointer.id]?.realChildren ?? []
                )
            )
        })

        if (errors.length > 0) {
            console.log(
                new ReportableError({
                    message: 'child is assigned to different parent',
                    errorType: 'illegalComponentStructure',
                    extras: {errors}
                })
            )
            return false
        }
        return true
    }

    const getNormalizedIdMap = (structure: Record<string, VirtualStructureEntryItem>) => {
        let currentKey = 0
        const stringMap: Record<string, string> = {}
        for (const [id, data] of Object.entries(structure)) {
            if (data.tag === VirtualStructureTag.Virtual) {
                stringMap[id] = `G_${(currentKey++).toString()}`
            }
        }
        return (key: string) => stringMap[key] ?? key
    }

    const createGroupingMap = async (
        {conversionDal, structureIterators, pageId, config, initialSnapshot}: StructureStageData,
        contextHelper: PluginContext
    ) => {
        const serverResponse = await groupingAIResult(
            structureIterators,
            conversionDal,
            pageId,
            pluginInitValues,
            contextHelper,
            config,
            initialSnapshot
        )

        if (!serverResponse) {
            return
        }

        contextHelper.set(CTX_AI_GROUPING_RESULT, serverResponse)
        const componentToChildrenGroupMap = createVirtualGroupMap(serverResponse, conversionDal)
        reconcileMissingChildren(conversionDal, componentToChildrenGroupMap)
        const isValidGroupingResult = validateGroupingResult(
            conversionDal,
            structureIterators,
            componentToChildrenGroupMap
        )

        if (!isValidGroupingResult) return

        const getNormalizedId = getNormalizedIdMap(componentToChildrenGroupMap)
        for (const [id, data] of Object.entries(componentToChildrenGroupMap)) {
            if (conversionDal.get(getStructurePointer({id}))) {
                conversionDal.modify(getStructurePointer({id}), value => ({
                    ...value,
                    virtualParentId: getNormalizedId(data.parentId)
                }))
            }

            conversionDal.set(getVirtualStructurePointer({id: getNormalizedId(id)}), {
                ...data,
                components: data.components.map(getNormalizedId)
            })
        }
    }

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(stages.GROUPING, createGroupingMap)
    }

    return {register, createApi, name: 'grouping', dependencies: []}
}
